import React, { useEffect, useState } from 'react';
import { Megaphone, Bell, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/programsApi';

const sel = 'h-9 border border-stone-200 rounded-lg px-2 text-xs text-stone-800 bg-white';
const PRESETS = [['', 'Since launch (all time)'], ['7', 'Last 7 days'], ['30', 'Last 30 days'], ['90', 'Last 90 days']];
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const Stat = ({ label, value, sub, testId }) => (
  <div className="rounded-xl border border-stone-200 bg-white p-3" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-stone-500">{label}</p>
    <p className="text-xl font-bold text-stone-800">{value}</p>
    {sub && <p className="text-[10.5px] text-stone-500">{sub}</p>}
  </div>
);

export const LaunchInsights = () => {
  const [preset, setPreset] = useState('');
  const [aid, setAid] = useState('');
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    adminApi.launchInsights({ announcementId: aid, dateFrom: preset ? daysAgo(Number(preset)) : '' }).then(setD).catch((e) => toast.error(e.message));
  }, [preset, aid]);
  const b = d?.banner, j = d?.journeys, p = d?.push;
  return (
    <div className="rounded-xl border border-[#C79A4A]/50 bg-[#FFFCF6] p-3 space-y-3" data-testid="launch-insights">
      <div className="flex flex-wrap items-center gap-2">
        <Megaphone className="w-4 h-4 text-[#C79A4A]" />
        <p className="text-sm font-semibold text-stone-800 flex-1">Launch Insights</p>
        {d && d.announcements.length > 1 && <select className={sel} value={aid} onChange={(e) => setAid(e.target.value)} data-testid="insights-announcement">{d.announcements.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}</select>}
        <select className={sel} value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="insights-range">{PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      </div>
      {!d ? <p className="text-xs text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p> : (
        <>
          <p className="text-[11px] text-stone-600" data-testid="insights-announcement-status">
            <b>{d.announcement.title}</b> · {d.announcement.programTitle} · today in {d.announcement.timezone}: <span className="font-semibold">{d.announcement.phase === 'banner' ? 'large banner' : d.announcement.phase === 'ribbon' ? 'ribbon' : 'ended'}</span>
            {' '}· banner through {d.announcement.schedule.bannerUntil}, ribbon {d.announcement.schedule.ribbonFrom} → {d.announcement.ribbonEnds}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Banner impressions" value={b.impressions} sub={`${b.uniqueViewers} unique viewers · Today ${b.impressionsByPlacement.today} / Programs ${b.impressionsByPlacement.programs}`} testId="insights-impressions" />
            <Stat label="Banner taps" value={b.taps} sub={`${b.uniqueTappers} unique · ${b.ctrPercent}% click-through`} testId="insights-taps" />
            <Stat label="Starts from banner" value={j.attributedStarts} sub={`${j.attributedActive} still active · 7-day attribution window`} testId="insights-attributed-starts" />
            <Stat label="Sessions after banner" value={j.attributedSessionsCompleted} sub="completed days by banner-attributed journeys" testId="insights-attributed-sessions" />
            <Stat label={`Full ${j.totalDays || 7}-day completions`} value={j.fullCompletionsAllSources} sub={`${j.attributedFullCompletions} of them entered via the banner`} testId="insights-full-completions" />
            <Stat label="Push opt-ins" value={p.optedInMembers} sub={`${p.optInRatePercent}% of ${p.members} members · ${p.devices} devices · ${p.optInsInRange} new in range`} testId="insights-push-optins" />
          </div>
          <div data-testid="insights-campaigns">
            <p className="text-[11px] font-semibold text-stone-700 mb-1 inline-flex items-center gap-1"><Bell className="w-3.5 h-3.5" /> Push campaign delivery</p>
            {p.campaigns.map((c) => (
              <div key={c.id} className="rounded-lg bg-white border border-stone-200 p-2 text-[11px] text-stone-700 flex flex-wrap gap-x-4 gap-y-1" data-testid={`insights-campaign-${c.id}`}>
                <span className="font-semibold">{c.name}</span>
                {c.sent ? <><span>Sent {fmt(c.sentAt)}</span><span>Delivered <b>{c.stats.sent}</b></span><span>Failed <b>{c.stats.failed}</b></span><span>Deferred <b>{c.stats.deferred}</b></span><span>Opened <b>{c.stats.opened}</b>{c.stats.openRate != null ? ` (${c.stats.openRate}%)` : ''}</span></>
                  : <span className="text-stone-500" data-testid={`insights-campaign-${c.id}-unsent`}>Not sent yet · {c.testSends} admin test send{c.testSends === 1 ? '' : 's'}</span>}
              </div>
            ))}
          </div>
          <p className="text-[10.5px] text-stone-400">Admin-only. Counts only — no names, emails, device endpoints or journal text are stored or shown. Anonymous viewers are counted by an opaque, random browser id.</p>
        </>
      )}
    </div>
  );
};
